import React, {FC, useState, useEffect, useRef} from "react"

import {MarketingProfile, UpdateMarketingProfileInput, WellnessLiveNotification} from "@irati/types/ops"
import tracking from "@irati-tracking/wellness"
import {FormattedMessage} from "@irati/utils/lang"
import mainTheme from "@irati/ui/src/global/theme"
import {Hr} from "@irati/ui/src/Layout"
import {H3, Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"

import {liveVideoMonth, liveVideoDayNumber, liveVideoDayText, liveVideoHours} from "@shared/wellness/utils"

import {WellnessMedia} from "@irati/types"
import {ReminderWrapper, CalendarDay, MonthSection, DateNumber, DateHours, Text, AnimatedButton} from "./styled"

interface Props {
  media: WellnessMedia,
  marketingProfile: MarketingProfile,
  handleClick: (marketingProfileInput: UpdateMarketingProfileInput) => void
}

const Reminder: FC<Props> = ({media, marketingProfile, handleClick}) => {
  const timerRef = useRef(null)
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false)
  const [startAnimation, setStartAnimation] = useState<boolean>(false)

  const {wellnessLiveNotifications} = marketingProfile?.settings
  const {uuid} = media

  useEffect(() => {
    const notificationEnabled = wellnessLiveNotifications?.find((el: WellnessLiveNotification) => el?.mediaId === uuid)
    if(notificationEnabled) {
      setNotificationsEnabled(true)
    }
    setStartAnimation(false)

    return () => clearTimeout(timerRef.current)
  }, [marketingProfile])

  const enableNotification = () => {
    if(wellnessLiveNotifications?.find((el: WellnessLiveNotification) => el?.mediaId === uuid)) {
      const filteredNotifications = wellnessLiveNotifications?.filter((el: WellnessLiveNotification) => el?.mediaId !== uuid)

      handleClick({
        uuid: marketingProfile.uuid,
        signature: marketingProfile.signature,
        settings: {
          ...marketingProfile.settings,
          wellnessLiveNotifications: filteredNotifications
        }
      })

      tracking.wellnessLiveDeregister()
      setNotificationsEnabled(false)
    } else {
      setStartAnimation(true)

      timerRef.current = setTimeout(() => {
        handleClick({
          uuid: marketingProfile.uuid,
          signature: marketingProfile.signature,
          settings: {
            ...marketingProfile.settings,
            wellnessLiveNotifications: [
              ...wellnessLiveNotifications,
              {
                mediaId: uuid,
                expiresAt: media.liveVideoStartTime
              }
            ]
          }
        })

        tracking.wellnessLiveRegister()
        setNotificationsEnabled(true)
      }, 500)
    }
  }

  return (
    <ReminderWrapper>
      <H3>
        <FormattedMessage id="wellness.liveVideoReminder.title" />
      </H3>
      <Hr />
      <CalendarDay direction="column" alignItems="center" justifyContent="center">
        <MonthSection justifyContent="center">{liveVideoMonth(media.liveVideoStartTime)}</MonthSection>
        <Flex direction="column" alignItems="center" flex="1">
          <DateNumber color={mainTheme.colors.greys.black}>{liveVideoDayNumber(media.liveVideoStartTime)}</DateNumber>
          <Body color={mainTheme.colors.greys.black}>{liveVideoDayText(media.liveVideoStartTime)}</Body>
        </Flex>
      </CalendarDay>
      <DateHours justifyContent="center">{liveVideoHours(media.liveVideoStartTime, media.liveVideoEndTime)}</DateHours>
      {
        notificationsEnabled ?
          <Button secondary full icon={IconTypes.notification} marginTop={5} onClick={enableNotification}>
            <Text>
              <FormattedMessage id="platform.reminder.cta.reminderEnabled" />
            </Text>
          </Button> :
          <AnimatedButton primary full marginTop={5} onClick={enableNotification} startAnimation={startAnimation}>
            <Flex alignItems="center">
              <Icon icon={IconTypes.notification} marginRight />
              <FormattedMessage id="platform.reminder.cta.setReminder" />
            </Flex>
          </AnimatedButton>
      }
    </ReminderWrapper>
  )
}

export default Reminder
